import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CheckCircle2, FileText, Filter, Loader2, XCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { logger } from '@/lib/logger';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { Spinner } from '@/components/ui/spinner';
import {
  getOrCreateHubConnection,
  ensureHubConnected,
  incrementSubscribers,
  decrementAndMaybeDisconnect,
} from '@/hooks/hubConnection';
import { useTriageStore, type TriageEditableField, type TriageResultDto, type TriageSourceType, uploadSourceKey } from '@/stores/useTriageStore';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';
import { TriageResultRow } from './TriageResultRow';

const EMPTY_TRIAGE_RESULTS: TriageResultDto[] = [];
const EMPTY_CONFIDENCE = { value: null, confidence: 0 };
const DOC_TYPE_OPTIONS = [
  { value: 'manual', label: 'Betriebsanleitung' },
  { value: 'datasheet', label: 'Datenblatt' },
  { value: 'service_manual', label: 'Servicehandbuch' },
  { value: 'drawing', label: 'Zeichnung' },
  { value: 'certificate', label: 'Zertifikat' },
  { value: 'report', label: 'Bericht' },
  { value: 'protocol', label: 'Protokoll' },
  { value: 'other', label: 'Sonstige' },
];
const LANGUAGE_OPTIONS = [
  { value: 'de', label: 'Deutsch' },
  { value: 'en', label: 'Englisch' },
  { value: 'fr', label: 'Französisch' },
  { value: 'es', label: 'Spanisch' },
  { value: 'it', label: 'Italienisch' },
  { value: 'pl', label: 'Polnisch' },
  { value: 'nl', label: 'Niederländisch' },
];

type UploadDraftFields = {
  model_name: string;
  series: string;
  manufacturer: string;
  category: string;
  doc_type: string;
  version: string;
  language: string;
};

const EMPTY_UPLOAD_DRAFT: UploadDraftFields = {
  model_name: '',
  series: '',
  manufacturer: '',
  category: '',
  doc_type: '',
  version: '',
  language: '',
};

const DOC_TYPE_HINTS: Array<{ needle: string; value: string }> = [
  { needle: 'betriebsanleitung', value: 'manual' },
  { needle: 'bedienungsanleitung', value: 'manual' },
  { needle: 'service', value: 'service_manual' },
  { needle: 'wartung', value: 'service_manual' },
  { needle: 'datenblatt', value: 'datasheet' },
  { needle: 'zeichnung', value: 'drawing' },
  { needle: 'zertifikat', value: 'certificate' },
  { needle: 'bericht', value: 'report' },
  { needle: 'protokoll', value: 'protocol' },
];

const LANGUAGE_HINTS: Array<{ needle: string; value: string }> = [
  { needle: 'deutsch', value: 'de' },
  { needle: 'german', value: 'de' },
  { needle: 'englisch', value: 'en' },
  { needle: 'english', value: 'en' },
  { needle: 'franz', value: 'fr' },
  { needle: 'spanisch', value: 'es' },
  { needle: 'italien', value: 'it' },
  { needle: 'niederl', value: 'nl' },
  { needle: 'poln', value: 'pl' },
];

function normalizeSuggestionText(value: string): string {
  return value
    .replace(/[\t ]+/g, ' ')
    .replace(/[;,]+$/g, '')
    .trim();
}

function mapSuggestionValue(field: keyof UploadDraftFields, value: string): string {
  if (field === 'doc_type') {
    const normalized = value.toLowerCase();
    // Direct enum match (LLM returns "Manual", "Drawing", etc.)
    const directMatch = DOC_TYPE_OPTIONS.find((opt) => opt.value === normalized);
    if (directMatch) return directMatch.value;
    return DOC_TYPE_HINTS.find((hint) => normalized.includes(hint.needle))?.value ?? value;
  }

  if (field === 'language') {
    const normalized = value.toLowerCase();
    if (/^de$|deutsch|german/.test(normalized)) return 'de';
    if (/^en$|englisch|english/.test(normalized)) return 'en';
    if (/^fr$|franz/.test(normalized)) return 'fr';
    if (/^es$|spanisch/.test(normalized)) return 'es';
    if (/^it$|italien/.test(normalized)) return 'it';
    if (/^nl$|niederl/.test(normalized)) return 'nl';
    if (/^pl$|poln/.test(normalized)) return 'pl';
  }

  return value;
}

function extractUploadSuggestionLines(rawText: string): string[] {
  if (!rawText) return [];

  const seen = new Set<string>();
  return rawText
    .split(/\r?\n/)
    .map((line) => normalizeSuggestionText(line))
    .filter((line) => line.length >= 2 && line.length <= 120)
    .filter((line) => {
      const dedupeKey = line.toLowerCase();
      if (seen.has(dedupeKey)) return false;
      seen.add(dedupeKey);
      return true;
    });
}

function detectLanguageFromText(rawText: string): string {
  const normalized = rawText.toLowerCase();
  const lines = extractUploadSuggestionLines(rawText).map((line) => line.toLowerCase());

  for (const hint of LANGUAGE_HINTS) {
    if (lines.includes(hint.value) || normalized.includes(hint.needle)) {
      return hint.value;
    }
  }

  if (lines.includes('de')) return 'de';
  if (lines.includes('en')) return 'en';
  if (lines.includes('fr')) return 'fr';
  if (lines.includes('es')) return 'es';
  if (lines.includes('it')) return 'it';
  if (lines.includes('nl')) return 'nl';
  if (lines.includes('pl')) return 'pl';

  return '';
}

function detectDocTypeFromText(rawText: string): string {
  const normalized = rawText.toLowerCase();
  const lines = extractUploadSuggestionLines(rawText).map((line) => line.toLowerCase());

  for (const hint of DOC_TYPE_HINTS) {
    if (lines.includes(hint.needle) || normalized.includes(hint.needle)) {
      return hint.value;
    }
  }

  return '';
}

const UploadPreviewPane = memo(function UploadPreviewPane({
  fileName,
  previewUrl,
  previewLoading,
}: {
  fileName: string;
  previewUrl: string | null;
  previewLoading: boolean;
}) {
  return (
    <section className="flex min-h-0 flex-col bg-muted/10">
      <div className="flex items-center gap-2 border-b px-4 py-3">
        <FileText className="h-4 w-4 text-muted-foreground" />
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Vorschau</p>
          <p className="text-xs text-muted-foreground">{fileName}</p>
        </div>
      </div>
      <div className="relative h-[38vh] overflow-auto bg-white sm:h-[46vh] lg:h-auto lg:flex-1">
        {previewLoading ? (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/80">
            <Spinner className="h-6 w-6 text-muted-foreground" />
          </div>
        ) : null}

        {previewUrl ? (
          <iframe
            src={`${previewUrl}#page=1&toolbar=0&navpanes=0&scrollbar=1&view=FitH`}
            title={`Vorschau ${fileName}`}
            className="h-full min-h-[38vh] w-full border-0 bg-white sm:min-h-[46vh] lg:min-h-[60vh]"
          />
        ) : !previewLoading ? (
          <div className="flex h-full min-h-[38vh] flex-col items-center justify-center gap-2 text-muted-foreground sm:min-h-[46vh] lg:min-h-[60vh]">
            <FileText className="h-10 w-10 opacity-30" />
            <p className="text-xs">Vorschau nicht verfügbar</p>
          </div>
        ) : null}
      </div>
    </section>
  );
});

function createUploadDraft(result: TriageResultDto | null): UploadDraftFields {
  if (!result) return EMPTY_UPLOAD_DRAFT;

  const rawTextDocType = detectDocTypeFromText(result.machine_metadata.raw_text_used ?? '');
  const rawTextLanguage = detectLanguageFromText(result.machine_metadata.raw_text_used ?? '');
  const normalizedDocType = mapSuggestionValue('doc_type', result.doc_type ?? rawTextDocType);

  return {
    model_name: result.machine_metadata.model_name?.value ?? '',
    series: result.machine_metadata.series?.value ?? '',
    manufacturer: result.machine_metadata.manufacturer?.value ?? '',
    category: result.machine_metadata.category?.value ?? '',
    doc_type: normalizedDocType,
    version: result.machine_metadata.version?.value ?? '',
    language: result.language ?? result.machine_metadata.language?.value ?? rawTextLanguage,
  };
}

interface ConnectorTriageBatchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** For connector triage: connectorId. For upload triage: use uploadSourceKey(workspaceId). */
  connectorId: string;
  workspaceId: string;
  sourceType?: TriageSourceType;
  connectorType?: string;
  configJson?: string;
  token?: string | null;
  onUploadConfirmed?: (documentIds: string[]) => void;
  onUploadRejected?: (documentIds: string[]) => Promise<void> | void;
}

function ConfidenceBadge({ confidence }: { confidence: number }) {
  const pct = Math.round(confidence * 100);
  const variant = confidence >= 0.8 ? 'default' : confidence >= 0.5 ? 'secondary' : 'destructive';
  return (
    <Badge variant={variant} className="text-xs tabular-nums">
      {pct}%
    </Badge>
  );
}

function fieldInputClass(confidence: number): string {
  const baseTypography = 'text-sm font-normal';
  if (confidence > 0.5) {
    return `${baseTypography} bg-amber-50 border-amber-300 dark:bg-amber-950/20 dark:border-amber-700/50`;
  }
  return baseTypography;
}

type LearnedFieldState = {
  source?: string | null;
  learned_from?: string | null;
  conflict_value?: string | null;
  conflict_confidence?: number | null;
  conflict_source?: string | null;
};

export function ConnectorTriageBatchDialog({
  open,
  onOpenChange,
  connectorId,
  workspaceId,
  sourceType = 'connector',
  connectorType,
  configJson,
  token,
  onUploadConfirmed,
  onUploadRejected,
}: ConnectorTriageBatchDialogProps) {
  const sourceKey = sourceType === 'upload' ? uploadSourceKey(workspaceId) : connectorId;
  const results = useTriageStore((s) => s.results[sourceKey] ?? EMPTY_TRIAGE_RESULTS);
  const triageStatus = useTriageStore((s) => s.status[sourceKey] || 'idle');
  const progress = useTriageStore((s) => s.progress[sourceKey]);
  const updateResultField = useTriageStore((s) => s.updateResultField);
  const updateResultStatus = useTriageStore((s) => s.updateResultStatus);
  const confirmAll = useTriageStore((s) => s.confirmAll);
  const recordDropMappingEvidence = useTriageStore((s) => s.recordDropMappingEvidence);
  const triggerConnectorTriage = useTriageStore((s) => s.triggerConnectorTriage);
  const submitConnectorConfirmation = useTriageStore((s) => s.submitConnectorConfirmation);
  const submitUploadConfirmation = useTriageStore((s) => s.submitUploadConfirmation);

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [confidenceFilter, setConfidenceFilter] = useState<string>('all');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [activeDocId, setActiveDocId] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [drafts, setDrafts] = useState<Record<string, UploadDraftFields>>({});
  const [mobileSection, setMobileSection] = useState<'preview' | 'parameters'>('preview');
  const [ignoredLearnedFields, setIgnoredLearnedFields] = useState<Record<string, Partial<Record<TriageEditableField, boolean>>>>({});
  const previewBlobUrlRef = useRef<string | null>(null);
  const isMobile = useIsMobile();

  const setTriageStatus = useTriageStore((s) => s.setTriageStatus);
  const setTriageProgress = useTriageStore((s) => s.setTriageProgress);
  const setTriageResults = useTriageStore((s) => s.setTriageResults);

  const activeUploadResult = useMemo(() => {
    if (sourceType !== 'upload') return null;
    return results.find((result) => result.doc_id === activeDocId) ?? results[0] ?? null;
  }, [activeDocId, results, sourceType]);

  const activeDraft = useMemo(() => {
    if (sourceType !== 'upload' || !activeUploadResult) return EMPTY_UPLOAD_DRAFT;
    return drafts[activeUploadResult.doc_id] ?? createUploadDraft(activeUploadResult);
  }, [activeUploadResult, drafts, sourceType]);

  const uploadSuggestionLines = useMemo(() => {
    if (sourceType !== 'upload' || !activeUploadResult) return [] as string[];
    return extractUploadSuggestionLines(activeUploadResult.machine_metadata.raw_text_used ?? '');
  }, [activeUploadResult, sourceType]);

  // ── SignalR triage event listeners ──────────────────────────────────────────
  useEffect(() => {
    if (!open) return;

    let cancelled = false;
    const connection = getOrCreateHubConnection();
    incrementSubscribers();

    // Connector triage events
    const onTriageStarted = (data: { connectorId: string }) => {
      if (cancelled || sourceType !== 'connector' || data.connectorId !== connectorId) return;
      setTriageStatus(sourceKey, 'running');
    };

    const onTriageProgress = (data: {
      connectorId: string;
      processed: number;
      total: number;
      currentFile: string;
    }) => {
      if (cancelled || sourceType !== 'connector' || data.connectorId !== connectorId) return;
      setTriageProgress(sourceKey, data.processed, data.total, data.currentFile ?? '');
    };

    const onTriageResults = (data: {
      connectorId: string;
      results: TriageResultDto[];
      total: number;
    }) => {
      if (cancelled || sourceType !== 'connector' || data.connectorId !== connectorId) return;
      setTriageResults(sourceKey, data.results);
      setTriageStatus(sourceKey, 'awaiting_confirmation');
    };

    // Upload triage events
    const onUploadTriageStarted = (data: { workspaceId: string }) => {
      if (cancelled || sourceType !== 'upload' || data.workspaceId !== workspaceId) return;
      setTriageStatus(sourceKey, 'running');
    };

    const onUploadTriageProgress = (data: {
      workspaceId: string;
      processed: number;
      total: number;
      currentFile: string;
    }) => {
      if (cancelled || sourceType !== 'upload' || data.workspaceId !== workspaceId) return;
      setTriageProgress(sourceKey, data.processed, data.total, data.currentFile ?? '');
    };

    const onUploadTriageResults = (data: {
      workspaceId: string;
      results: TriageResultDto[];
      total: number;
    }) => {
      if (cancelled || sourceType !== 'upload' || data.workspaceId !== workspaceId) return;
      useTriageStore.getState().appendTriageResults(sourceKey, data.results);
      setTriageStatus(sourceKey, 'awaiting_confirmation');
    };

    connection.on('ConnectorTriageStarted', onTriageStarted);
    connection.on('ConnectorTriageProgress', onTriageProgress);
    connection.on('ConnectorTriageResults', onTriageResults);
    connection.on('UploadTriageStarted', onUploadTriageStarted);
    connection.on('UploadTriageProgress', onUploadTriageProgress);
    connection.on('UploadTriageResults', onUploadTriageResults);

    ensureHubConnected(connection).catch((err) => {
      if (!cancelled) {
        logger.error('TriageBatchDialog', 'Connection error', err);
      }
    });

    return () => {
      cancelled = true;
      connection.off('ConnectorTriageStarted', onTriageStarted);
      connection.off('ConnectorTriageProgress', onTriageProgress);
      connection.off('ConnectorTriageResults', onTriageResults);
      connection.off('UploadTriageStarted', onUploadTriageStarted);
      connection.off('UploadTriageProgress', onUploadTriageProgress);
      connection.off('UploadTriageResults', onUploadTriageResults);
      decrementAndMaybeDisconnect();
    };
  }, [open, connectorId, workspaceId, sourceType, sourceKey, setTriageStatus, setTriageProgress, setTriageResults]);

  useEffect(() => {
    if (sourceType !== 'upload') return;
    if (results.length === 0) {
      setActiveDocId(null);
      return;
    }

    setActiveDocId((current) => {
      if (current && results.some((result) => result.doc_id === current)) {
        return current;
      }
      return results[0]?.doc_id ?? null;
    });
  }, [results, sourceType]);

  useEffect(() => {
    if (sourceType !== 'upload') return;
    setDrafts((current) => {
      const next = { ...current };
      let changed = false;

      for (const result of results) {
        if (!next[result.doc_id]) {
          const draft = createUploadDraft(result);
          next[result.doc_id] = draft;
          changed = true;

          // Sync mapped doc_type back to store if it resolved to a valid option
          const mappedDocType = draft.doc_type;
          if (mappedDocType && DOC_TYPE_OPTIONS.some((opt) => opt.value === mappedDocType)) {
            const origNorm = (result.doc_type ?? '').toLowerCase();
            if (origNorm !== mappedDocType || result.doc_type_confidence < 0.01) {
              updateResultField(sourceKey, result.doc_id, 'doc_type', mappedDocType);
            }
          }
          // Sync mapped language back to store if it resolved to a valid option
          const mappedLang = draft.language;
          if (mappedLang && LANGUAGE_OPTIONS.some((opt) => opt.value === mappedLang)) {
            const origLang = result.language ?? '';
            if (origLang !== mappedLang) {
              updateResultField(sourceKey, result.doc_id, 'language', mappedLang);
            }
          }
        }
      }

      for (const docId of Object.keys(next)) {
        if (!results.some((result) => result.doc_id === docId)) {
          delete next[docId];
          changed = true;
        }
      }

      return changed ? next : current;
    });
  }, [results, sourceType, sourceKey, updateResultField]);

  useEffect(() => {
    if (sourceType !== 'upload' || !open || !activeUploadResult) {
      if (previewBlobUrlRef.current) {
        URL.revokeObjectURL(previewBlobUrlRef.current);
        previewBlobUrlRef.current = null;
      }
      setPreviewUrl(null);
      setPreviewLoading(false);
      return;
    }

    let cancelled = false;
    setPreviewLoading(true);

    void (async () => {
      try {
        const headers: HeadersInit = {};
        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }

        const response = await fetch(`/api/dotnet/documents/${activeUploadResult.doc_id}/view`, { headers });
        if (!response.ok) {
          throw new Error(`Preview fetch failed with status ${response.status}`);
        }

        const blob = await response.blob();
        const objectUrl = URL.createObjectURL(blob);

        if (cancelled) {
          URL.revokeObjectURL(objectUrl);
          return;
        }

        if (previewBlobUrlRef.current) {
          URL.revokeObjectURL(previewBlobUrlRef.current);
        }

        previewBlobUrlRef.current = objectUrl;
        setPreviewUrl(objectUrl);
      } catch {
        if (!cancelled) {
          setPreviewUrl(null);
        }
      } finally {
        if (!cancelled) {
          setPreviewLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [activeUploadResult, open, sourceType, token]);

  // Filter results
  const filteredResults = useMemo(() => {
    if (confidenceFilter === 'all') return results;
    if (confidenceFilter === 'high') return results.filter((r) => r.overall_confidence >= 0.8);
    if (confidenceFilter === 'medium') return results.filter((r) => r.overall_confidence >= 0.5 && r.overall_confidence < 0.8);
    if (confidenceFilter === 'low') return results.filter((r) => r.overall_confidence < 0.5);
    return results;
  }, [results, confidenceFilter]);

  // Stats
  const stats = useMemo(() => {
    const confirmed = results.filter((r) => r.triage_status === 'confirmed').length;
    const rejected = results.filter((r) => r.triage_status === 'rejected').length;
    const pending = results.filter((r) => r.triage_status === 'pending').length;
    const highConf = results.filter((r) => r.overall_confidence >= 0.8).length;
    return { confirmed, rejected, pending, highConf, total: results.length };
  }, [results]);

  const handleSelect = useCallback((docId: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(docId);
      else next.delete(docId);
      return next;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    if (selectedIds.size === filteredResults.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredResults.map((r) => r.doc_id)));
    }
  }, [filteredResults, selectedIds.size]);

  const handleConfirmAll = useCallback(() => {
    confirmAll(sourceKey);
  }, [confirmAll, sourceKey]);

  const handleSubmit = useCallback(async () => {
    setSubmitError(null);
    setSubmitting(true);

    if (sourceType === 'upload') {
      for (const result of results) {
        const draft = drafts[result.doc_id];
        if (!draft) continue;
        updateResultField(sourceKey, result.doc_id, 'model_name', draft.model_name);
        updateResultField(sourceKey, result.doc_id, 'series', draft.series);
        updateResultField(sourceKey, result.doc_id, 'manufacturer', draft.manufacturer);
        updateResultField(sourceKey, result.doc_id, 'category', draft.category);
        updateResultField(sourceKey, result.doc_id, 'doc_type', draft.doc_type);
        updateResultField(sourceKey, result.doc_id, 'version', draft.version);
        updateResultField(sourceKey, result.doc_id, 'language', draft.language);
      }

      for (const result of results) {
        if (result.triage_status === 'pending') {
          updateResultStatus(sourceKey, result.doc_id, 'confirmed');
        }
      }
    }

    const confirmedDocIds = sourceType === 'upload'
      ? results
        .filter((result) => result.triage_status !== 'rejected')
        .map((result) => result.doc_id)
      : results
        .filter((result) => result.triage_status === 'confirmed')
        .map((result) => result.doc_id);
    const rejectedDocIds = results
      .filter((result) => result.triage_status === 'rejected')
      .map((result) => result.doc_id);

    let success = false;
    if (sourceType === 'upload') {
      success = await submitUploadConfirmation(workspaceId, token);
    } else {
      success = await submitConnectorConfirmation(workspaceId, connectorId, token);
    }

    setSubmitting(false);

    if (!success) {
      setSubmitError('Die Bestätigung konnte nicht gespeichert werden.');
      return;
    }

    if (sourceType === 'upload' && confirmedDocIds.length > 0) {
      onUploadConfirmed?.(confirmedDocIds);
    }

    if (sourceType === 'upload' && rejectedDocIds.length > 0) {
      await onUploadRejected?.(rejectedDocIds);
    }

    onOpenChange(false);
  }, [connectorId, drafts, onOpenChange, onUploadConfirmed, onUploadRejected, results, sourceKey, sourceType, submitConnectorConfirmation, submitUploadConfirmation, token, updateResultField, updateResultStatus, workspaceId]);

  const updateActiveDraftField = useCallback((field: keyof UploadDraftFields, value: string) => {
    if (!activeUploadResult) return;
    setDrafts((current) => ({
      ...current,
      [activeUploadResult.doc_id]: {
        ...(current[activeUploadResult.doc_id] ?? createUploadDraft(activeUploadResult)),
        [field]: value,
      },
    }));
  }, [activeUploadResult]);

  const commitDraftField = useCallback((field: keyof UploadDraftFields & TriageEditableField) => {
    if (!activeUploadResult) return;
    const draft = drafts[activeUploadResult.doc_id] ?? createUploadDraft(activeUploadResult);
    updateResultField(sourceKey, activeUploadResult.doc_id, field, draft[field]);
  }, [activeUploadResult, drafts, sourceKey, updateResultField]);

  const applySuggestion = useCallback((field: keyof UploadDraftFields, value: string) => {
    if (!activeUploadResult) return;
    const mappedValue = mapSuggestionValue(field, value);

    setDrafts((current) => ({
      ...current,
      [activeUploadResult.doc_id]: {
        ...(current[activeUploadResult.doc_id] ?? createUploadDraft(activeUploadResult)),
        [field]: mappedValue,
      },
    }));

    updateResultField(sourceKey, activeUploadResult.doc_id, field as keyof UploadDraftFields & TriageEditableField, mappedValue);
    recordDropMappingEvidence(sourceKey, activeUploadResult.doc_id, {
      source_text: value,
      target_field: field as keyof UploadDraftFields & TriageEditableField,
      applied_value: mappedValue,
    });
    setIgnoredLearnedFields((current) => ({
      ...current,
      [activeUploadResult.doc_id]: {
        ...(current[activeUploadResult.doc_id] ?? {}),
        [field as TriageEditableField]: false,
      },
    }));
  }, [activeUploadResult, recordDropMappingEvidence, sourceKey, updateResultField]);

  const createDropHandler = useCallback((field: keyof UploadDraftFields) => {
    return (event: React.DragEvent<HTMLElement>) => {
      event.preventDefault();
      const droppedValue = event.dataTransfer.getData('text/plain');
      if (!droppedValue) return;
      applySuggestion(field, droppedValue);
    };
  }, [applySuggestion]);

  const handleStartTriage = useCallback(async () => {
    if (sourceType === 'connector') {
      if (!connectorType || !configJson) return;
      await triggerConnectorTriage(workspaceId, connectorId, connectorType, configJson, token);
    }
  }, [triggerConnectorTriage, sourceType, workspaceId, connectorId, connectorType, configJson, token]);

  const importableCount = stats.total - stats.rejected;
  const canSubmitDecision = sourceType === 'upload'
    ? stats.total > 0
    : stats.total > 0 && stats.pending === 0;

  const submitLabel = useMemo(() => {
    if (submitting) {
      return 'Sende…';
    }

    if (sourceType === 'upload' && importableCount > 0) {
      return `${importableCount} Dokument${importableCount !== 1 ? 'e' : ''} importieren`;
    }

    if (stats.confirmed > 0) {
      return `${stats.confirmed} Dokument${stats.confirmed !== 1 ? 'e' : ''} importieren`;
    }

    if (stats.rejected === stats.total && stats.total > 0) {
      return 'Ablehnung abschließen';
    }

    return 'Entscheidungen speichern';
  }, [importableCount, sourceType, stats.confirmed, stats.rejected, stats.total, submitting]);

  const renderUploadReview = () => {
    if (isRunning && !activeUploadResult) {
      return null;
    }

    if (!activeUploadResult) {
      return (
        <div className="flex flex-1 items-center justify-center text-sm text-muted-foreground">
          Warten auf Triage-Ergebnisse…
        </div>
      );
    }

    const activeMetadata = activeUploadResult.machine_metadata;
    const categoryField = activeMetadata.category ?? EMPTY_CONFIDENCE;
    const modelField = activeMetadata.model_name ?? EMPTY_CONFIDENCE;
    const seriesField = activeMetadata.series ?? EMPTY_CONFIDENCE;
    const manufacturerField = activeMetadata.manufacturer ?? EMPTY_CONFIDENCE;
    const versionField = activeMetadata.version ?? EMPTY_CONFIDENCE;
    const languageField = activeMetadata.language ?? EMPTY_CONFIDENCE;

    const renderLearnedBadge = (field: { source?: string | null; learned_from?: string | null }) => {
      if (field.source !== 'learned') return null;
      return (
        <Badge variant="outline" title={field.learned_from ? `Gelernt aus: ${field.learned_from}` : 'Gelernt'}>
          Gelernt
        </Badge>
      );
    };

    const isLearnedIgnored = (fieldName: TriageEditableField) =>
      Boolean(ignoredLearnedFields[activeUploadResult.doc_id]?.[fieldName]);

    const renderConflictHint = (fieldName: TriageEditableField, field: LearnedFieldState) => {
      if (field.source !== 'learned' || isLearnedIgnored(fieldName) || !field.conflict_value) {
        return null;
      }

      const confidenceLabel = typeof field.conflict_confidence === 'number'
        ? ` (${Math.round(field.conflict_confidence * 100)}%)`
        : '';
      const sourceLabel = field.conflict_source === 'triage' || !field.conflict_source
        ? 'Triage-Vorschlag'
        : field.conflict_source;

      return (
        <div className="rounded-md border border-amber-300/70 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-700/60 dark:bg-amber-950/20 dark:text-amber-100">
          <p className="font-medium">Konflikt zwischen Lernmuster und bestehendem Vorschlag</p>
          <p className="mt-1">
            Lernmuster aktiv, ursprünglicher Vorschlag: <span className="font-medium">{field.conflict_value}</span>
            {confidenceLabel} aus {sourceLabel}.
          </p>
        </div>
      );
    };

    const ignoreLearnedSuggestion = (field: TriageEditableField, fieldState: LearnedFieldState) => {
      if (!activeUploadResult) return;

      const fallbackValue = fieldState.conflict_value ?? '';
      setIgnoredLearnedFields((current) => ({
        ...current,
        [activeUploadResult.doc_id]: {
          ...(current[activeUploadResult.doc_id] ?? {}),
          [field]: true,
        },
      }));

      setDrafts((current) => ({
        ...current,
        [activeUploadResult.doc_id]: {
          ...(current[activeUploadResult.doc_id] ?? createUploadDraft(activeUploadResult)),
          [field]: fallbackValue,
        },
      }));

      updateResultField(sourceKey, activeUploadResult.doc_id, field, fallbackValue);
    };

    const renderLearnedActions = (fieldName: TriageEditableField, field: LearnedFieldState) => {
      if (field.source !== 'learned' || isLearnedIgnored(fieldName)) return null;

      return (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-6 px-2 text-xs text-muted-foreground"
          aria-label="Lernvorschlag ignorieren"
          data-testid={`ignore-learned-${fieldName}`}
          onClick={() => ignoreLearnedSuggestion(fieldName, field)}
        >
          Ignorieren
        </Button>
      );
    };

    const renderIgnoredHint = (fieldName: TriageEditableField) => {
      if (!isLearnedIgnored(fieldName)) return null;
      return (
        <p className="text-xs text-muted-foreground">
          Lernvorschlag für dieses Feld in diesem Dialog ignoriert.
        </p>
      );
    };

    const renderDocumentSidebar = (className?: string) => (
      <aside className={cn('flex min-h-0 flex-col bg-muted/20', className)}>
        <div className="border-b px-4 py-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Dokumente
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            {stats.total} Datei{stats.total !== 1 ? 'en' : ''} zur Freigabe
          </p>
        </div>
        <div className="max-h-44 flex-1 space-y-2 overflow-y-auto p-3 sm:max-h-52 md:max-h-none">
          {results.map((result) => {
            const isActive = result.doc_id === activeUploadResult.doc_id;
            const isConfirmed = result.triage_status === 'confirmed';
            const isRejected = result.triage_status === 'rejected';

            return (
              <button
                key={result.doc_id}
                type="button"
                onClick={() => {
                  setActiveDocId(result.doc_id);
                  setMobileSection('preview');
                }}
                className={cn(
                  'w-full rounded-lg border px-3 py-3 text-left transition-colors',
                  isActive ? 'border-primary bg-primary/5 shadow-sm' : 'border-border bg-background hover:border-primary/40',
                  isRejected && 'opacity-75',
                )}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">{result.file_name}</p>
                    <p className="mt-1 truncate text-xs text-muted-foreground">
                      {result.machine_metadata.model_name?.value || 'Kein Modell erkannt'}
                    </p>
                  </div>
                  <ConfidenceBadge confidence={result.overall_confidence} />
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <Badge variant={isConfirmed ? 'default' : isRejected ? 'destructive' : 'secondary'}>
                    {isConfirmed ? 'Bestätigt' : isRejected ? 'Abgelehnt' : 'Offen'}
                  </Badge>
                  {result.doc_type ? (
                    <Badge variant="outline" className="max-w-[120px] truncate">
                      {result.doc_type}
                    </Badge>
                  ) : null}
                </div>
              </button>
            );
          })}
        </div>
      </aside>
    );

    const renderParameterPane = (className?: string) => (
      <section className={cn('flex min-h-0 flex-col border-t bg-background', className)}>
        <div className="border-b px-5 py-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <p className="text-sm font-semibold">Parameter</p>
              <p className="mt-1 text-xs text-muted-foreground">
                Maschinen- und Dokumentmetadaten prüfen und vervollständigen.
              </p>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <Button
                type="button"
                size="sm"
                className="w-full sm:w-auto"
                variant={activeUploadResult.triage_status === 'confirmed' ? 'default' : 'outline'}
                onClick={() => updateResultStatus(sourceKey, activeUploadResult.doc_id, 'confirmed')}
              >
                Bestätigen
              </Button>
              <Button
                type="button"
                size="sm"
                className="w-full sm:w-auto"
                variant={activeUploadResult.triage_status === 'rejected' ? 'destructive' : 'outline'}
                onClick={() => updateResultStatus(sourceKey, activeUploadResult.doc_id, 'rejected')}
              >
                Ablehnen
              </Button>
            </div>
          </div>
        </div>

        <div className="flex-1 space-y-5 overflow-y-auto p-4 sm:p-5">
          {uploadSuggestionLines.length > 0 ? (
            <div className="rounded-md border bg-muted/15 p-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Textauszug</p>
              <p className="mt-1 text-xs text-muted-foreground">
                Jede erkannte Zeile kann per Drag-and-Drop in ein beliebiges Zielfeld gezogen werden.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                {uploadSuggestionLines.map((line) => (
                  <button
                    key={line}
                    type="button"
                    draggable
                    onDragStart={(event) => {
                      event.dataTransfer.setData('text/plain', line);
                      event.dataTransfer.effectAllowed = 'copy';
                    }}
                    className="inline-flex max-w-full cursor-grab items-center rounded-full border border-border/70 bg-muted px-3 py-1.5 text-[11px] leading-4 font-normal text-foreground transition hover:bg-muted/80 active:cursor-grabbing"
                    title="Mit Drag-and-Drop in ein Feld ziehen"
                  >
                    <span className="truncate text-[11px] leading-4">{line}</span>
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          <div className="grid gap-4">
            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <Label htmlFor="triage-model-name">Modellname</Label>
                <ConfidenceBadge confidence={modelField.confidence} />
                {renderLearnedBadge(modelField)}
                {renderLearnedActions('model_name', modelField)}
              </div>
              {renderConflictHint('model_name', modelField)}
              <Input
                id="triage-model-name"
                value={activeDraft.model_name}
                onChange={(event) => updateActiveDraftField('model_name', event.target.value)}
                onBlur={() => commitDraftField('model_name')}
                onDragOver={(event) => event.preventDefault()}
                onDrop={createDropHandler('model_name')}
                placeholder="z.B. XR-5000"
                className={fieldInputClass(modelField.confidence)}
              />
              {renderIgnoredHint('model_name')}
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <Label htmlFor="triage-series">Serie</Label>
                <ConfidenceBadge confidence={seriesField.confidence} />
                {renderLearnedBadge(seriesField)}
                {renderLearnedActions('series', seriesField)}
              </div>
              {renderConflictHint('series', seriesField)}
              <Input
                id="triage-series"
                value={activeDraft.series}
                onChange={(event) => updateActiveDraftField('series', event.target.value)}
                onBlur={() => commitDraftField('series')}
                onDragOver={(event) => event.preventDefault()}
                onDrop={createDropHandler('series')}
                placeholder="z.B. XR-Serie"
                className={fieldInputClass(seriesField.confidence)}
              />
              {renderIgnoredHint('series')}
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <Label htmlFor="triage-manufacturer">Hersteller</Label>
                <ConfidenceBadge confidence={manufacturerField.confidence} />
                {renderLearnedBadge(manufacturerField)}
                {renderLearnedActions('manufacturer', manufacturerField)}
              </div>
              {renderConflictHint('manufacturer', manufacturerField)}
              <Input
                id="triage-manufacturer"
                value={activeDraft.manufacturer}
                onChange={(event) => updateActiveDraftField('manufacturer', event.target.value)}
                onBlur={() => commitDraftField('manufacturer')}
                onDragOver={(event) => event.preventDefault()}
                onDrop={createDropHandler('manufacturer')}
                placeholder="z.B. Siemens"
                className={fieldInputClass(manufacturerField.confidence)}
              />
              {renderIgnoredHint('manufacturer')}
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <Label htmlFor="triage-category">Kategorie</Label>
                <ConfidenceBadge confidence={categoryField.confidence} />
                {renderLearnedBadge(categoryField)}
                {renderLearnedActions('category', categoryField)}
              </div>
              {renderConflictHint('category', categoryField)}
              <Input
                id="triage-category"
                value={activeDraft.category}
                onChange={(event) => updateActiveDraftField('category', event.target.value)}
                onBlur={() => commitDraftField('category')}
                onDragOver={(event) => event.preventDefault()}
                onDrop={createDropHandler('category')}
                placeholder="z.B. CNC-Maschine"
                className={fieldInputClass(categoryField.confidence)}
              />
              {renderIgnoredHint('category')}
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <Label htmlFor="triage-doc-type">Dokumenttyp</Label>
                <ConfidenceBadge confidence={activeUploadResult.doc_type_confidence} />
                {renderLearnedBadge(activeMetadata.doc_type ?? EMPTY_CONFIDENCE)}
                {renderLearnedActions('doc_type', activeMetadata.doc_type ?? EMPTY_CONFIDENCE)}
              </div>
              {renderConflictHint('doc_type', activeMetadata.doc_type ?? EMPTY_CONFIDENCE)}
              <div onDragOver={(event) => event.preventDefault()} onDrop={createDropHandler('doc_type')}>
                <Select
                  value={activeDraft.doc_type}
                  onValueChange={(value) => {
                    updateActiveDraftField('doc_type', value);
                    updateResultField(sourceKey, activeUploadResult.doc_id, 'doc_type', value);
                  }}
                >
                  <SelectTrigger id="triage-doc-type" className={cn(fieldInputClass(activeUploadResult.doc_type_confidence), '[&>span]:text-sm [&>span]:font-normal')}>
                    <SelectValue placeholder="Auswählen…" />
                  </SelectTrigger>
                  <SelectContent>
                    {DOC_TYPE_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {renderIgnoredHint('doc_type')}
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <Label htmlFor="triage-version">Version</Label>
                <ConfidenceBadge confidence={versionField.confidence} />
                {renderLearnedBadge(versionField)}
                {renderLearnedActions('version', versionField)}
              </div>
              {renderConflictHint('version', versionField)}
              <Input
                id="triage-version"
                value={activeDraft.version}
                onChange={(event) => updateActiveDraftField('version', event.target.value)}
                onBlur={() => commitDraftField('version')}
                onDragOver={(event) => event.preventDefault()}
                onDrop={createDropHandler('version')}
                placeholder="z.B. 2.1"
                className={fieldInputClass(versionField.confidence)}
              />
              {renderIgnoredHint('version')}
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <Label htmlFor="triage-language">Sprache</Label>
                <ConfidenceBadge confidence={languageField.confidence} />
                {renderLearnedBadge(languageField)}
                {renderLearnedActions('language', languageField)}
              </div>
              {renderConflictHint('language', languageField)}
              <div onDragOver={(event) => event.preventDefault()} onDrop={createDropHandler('language')}>
                <Select
                  value={activeDraft.language}
                  onValueChange={(value) => {
                    updateActiveDraftField('language', value);
                    updateResultField(sourceKey, activeUploadResult.doc_id, 'language', value);
                  }}
                >
                  <SelectTrigger id="triage-language" className={cn(fieldInputClass(languageField.confidence), '[&>span]:text-sm [&>span]:font-normal')}>
                    <SelectValue placeholder="Auswählen…" />
                  </SelectTrigger>
                  <SelectContent>
                    {LANGUAGE_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {renderIgnoredHint('language')}
            </div>
          </div>

          {activeUploadResult.summary ? (
            <div className="rounded-md border bg-muted/20 p-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Zusammenfassung</p>
              <p className="mt-1 text-sm text-foreground/90">{activeUploadResult.summary}</p>
            </div>
          ) : null}

          {submitError ? (
            <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {submitError}
            </div>
          ) : null}
        </div>
      </section>
    );

    if (!isMobile) {
      return (
        <div className="grid min-h-0 flex-1 overflow-hidden rounded-md border [grid-template-columns:220px_minmax(0,1.45fr)_360px] xl:[grid-template-columns:240px_minmax(0,1.55fr)_380px]">
          {renderDocumentSidebar('border-r')}
          <UploadPreviewPane
            fileName={activeUploadResult.file_name}
            previewLoading={previewLoading}
            previewUrl={previewUrl}
          />
          {renderParameterPane('border-l border-t-0')}
        </div>
      );
    }

    return (
      <div className="flex min-h-0 flex-1 flex-col gap-3">
        {renderDocumentSidebar()}

        <Tabs
          value={mobileSection}
          onValueChange={(value) => setMobileSection(value as 'preview' | 'parameters')}
          className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-md border"
        >
          <TabsList className="mx-3 mt-3 grid w-auto grid-cols-2">
            <TabsTrigger value="preview">Vorschau</TabsTrigger>
            <TabsTrigger value="parameters">Parameter</TabsTrigger>
          </TabsList>
          <TabsContent value="preview" className="mt-3 min-h-0 flex-1 overflow-hidden border-t">
            <UploadPreviewPane
              fileName={activeUploadResult.file_name}
              previewLoading={previewLoading}
              previewUrl={previewUrl}
            />
          </TabsContent>
          <TabsContent value="parameters" className="mt-3 min-h-0 flex-1 overflow-hidden border-t">
            {renderParameterPane('border-t-0')}
          </TabsContent>
        </Tabs>
      </div>
    );
  };

  const isRunning = triageStatus === 'running';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[92vh] w-[98vw] max-w-[98vw] flex-col overflow-hidden p-4 sm:w-[96vw] sm:max-w-[96vw] sm:p-6 lg:h-[90vh] lg:w-[1400px] lg:max-w-[1400px]">
        <DialogHeader>
          <DialogTitle>{sourceType === 'upload' ? 'Dokumenten-Klassifizierung' : 'Triage-Ergebnisse'}</DialogTitle>
          <DialogDescription>
            {isRunning
              ? 'Dokumente werden klassifiziert…'
              : `${stats.total} Dateien erkannt — ${stats.highConf} hochkonfident`}
          </DialogDescription>
        </DialogHeader>

        {/* Progress bar during triage */}
        {isRunning && progress && (
          <div className="space-y-2">
            <Progress value={(progress.processed / Math.max(progress.total, 1)) * 100} />
            <p className="text-xs text-muted-foreground">
              {progress.processed} / {progress.total} — {progress.currentFile}
            </p>
          </div>
        )}

        {/* Stats bar */}
        {!isRunning && stats.total > 0 && (
          <div className="flex flex-wrap items-center gap-2 text-sm sm:gap-3">
            <Badge variant="default" className="gap-1">
              <CheckCircle2 className="h-3 w-3" />
              {stats.confirmed} bestätigt
            </Badge>
            <Badge variant="destructive" className="gap-1">
              <XCircle className="h-3 w-3" />
              {stats.rejected} abgelehnt
            </Badge>
            <Badge variant="secondary">
              {stats.pending} offen
            </Badge>
            <div className="ml-auto flex items-center gap-2 max-sm:w-full max-sm:justify-end">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <Select value={confidenceFilter} onValueChange={setConfidenceFilter}>
                <SelectTrigger className="h-7 w-[120px] sm:w-[140px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle</SelectItem>
                  <SelectItem value="high">Hoch (&ge;80%)</SelectItem>
                  <SelectItem value="medium">Mittel (50-79%)</SelectItem>
                  <SelectItem value="low">Niedrig (&lt;50%)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {sourceType === 'upload' && !isRunning && results.length > 0 && renderUploadReview()}

        {/* Results table */}
        {sourceType !== 'upload' && !isRunning && filteredResults.length > 0 && (
          <div className="overflow-auto flex-1 border rounded-md">
            <table className="w-full text-left">
              <thead className="sticky top-0 bg-muted/80 backdrop-blur">
                <tr className="border-b">
                  <th className="px-3 py-2 w-8">
                    <input
                      type="checkbox"
                      checked={selectedIds.size === filteredResults.length && filteredResults.length > 0}
                      onChange={handleSelectAll}
                      className="rounded"
                    />
                  </th>
                  <th className="px-3 py-2 text-xs font-medium text-muted-foreground">Datei</th>
                  <th className="px-3 py-2 text-xs font-medium text-muted-foreground">Typ</th>
                  <th className="px-3 py-2 text-xs font-medium text-muted-foreground">Typ-Konf.</th>
                  <th className="px-3 py-2 text-xs font-medium text-muted-foreground">Maschine</th>
                  <th className="px-3 py-2 text-xs font-medium text-muted-foreground">M-Konf.</th>
                  <th className="px-3 py-2 text-xs font-medium text-muted-foreground">Gesamt</th>
                  <th className="px-3 py-2 text-xs font-medium text-muted-foreground">Aktion</th>
                </tr>
              </thead>
              <tbody>
                {filteredResults.map((result) => (
                  <TriageResultRow
                    key={result.doc_id}
                    result={result}
                    selected={selectedIds.has(result.doc_id)}
                    onSelect={handleSelect}
                    onUpdateField={(docId, field, value) =>
                      updateResultField(sourceKey, docId, field, value)
                    }
                    onUpdateStatus={(docId, status) =>
                      updateResultStatus(sourceKey, docId, status)
                    }
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Empty state */}
        {!isRunning && results.length === 0 && (
          <div className="flex flex-col items-center gap-4 py-8 text-muted-foreground">
            <p>Keine Triage-Ergebnisse vorhanden.</p>
            {sourceType === 'connector' && connectorType && configJson && (
              <Button onClick={() => void handleStartTriage()}>
                Triage starten
              </Button>
            )}
          </div>
        )}

        <DialogFooter className="gap-2 border-t pt-3 sm:gap-2">
          <Button
            variant="outline"
            className="w-full sm:w-auto"
            onClick={handleConfirmAll}
            disabled={isRunning || stats.total === 0}
          >
            Alle bestätigen
          </Button>
          <Button
            className="w-full sm:w-auto"
            onClick={handleSubmit}
            disabled={isRunning || submitting || !canSubmitDecision}
          >
            {submitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Sende…
              </>
            ) : (
              submitLabel
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
